import { Box, Dialog, Grid, MenuItem, Typography } from "@material-ui/core";
import MROButton from "components/buttons";
import LoadingIndicator from "components/universal/loading_indicator";
import { ToastMessageContext } from "lib/contexts/message_context";
import React, { useContext, useEffect, useState } from "react";
import useGetReportFormDropdowns from "./useGetReportFormDropdowns";
import MROTextField from "components/form_components/TextField";
import SMSReportToggle from "views/SMS/MySMS/Header/SMSReportToggle";
import DateTimeField from "components/form_components/LMEventsDateTimeField";
import DateField from "components/form_components/DateField";
import PlanningTimeField from "components/form_components/PlanningTimeField";
import moment from "moment";
import { getOrganisationId, getUserId } from "lib/utils/common_utils";
import ReportsServices from "lib/services/api/reports/reports";

const yesOrNoToggleOptions = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes" },
];

const ReportsAction = (props) => {
  const { handleCloseAction, currentSelection, refreshListing } = props;

  const message = useContext(ToastMessageContext);

  const [fieldsList, setFieldsList] = useState([]);

  const [reportsData, setReportsData] = useState({
    report_name: "",
    description: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);

  const { loading: isDropdownsLoading, dropdownOptions } =
    useGetReportFormDropdowns();

  const getFieldsList = async () => {
    setLoading(true);
    try {
      const params = {
        report_id: currentSelection?.id,
      };

      const response = await ReportsServices.getReportParams(params);
      if (response?.success && response?.data) {
        setReportsData({
          report_name: response?.data?.report_name,
          description: response?.data?.description,
        });

        const semiFixedFieldsArray = [];

        const fieldMappings = {
          base_stations: {
            name: "base_station_id",
            title: "Base",
            description: "Select the base location.",
            type: "dropdown",
            default: "all",
          },
          line_customers: {
            name: "line_customer_id",
            title: "Customer",
            description: "Select customer.",
            type: "dropdown",
            default: "all",
          },
          users: {
            name: "user_id",
            title: "User",
            description: "Select User.",
            type: "dropdown",
            default: "all",
          },
        };
        Object.entries(fieldMappings).forEach(([key, field]) => {
          if (response?.data?.[key]) {
            semiFixedFieldsArray.push({ ...field });
          }
        });
        const dynamicFields = Object.values(response?.data?.params || {});
        const transformedFieldArray = [
          ...semiFixedFieldsArray,
          ...(dynamicFields || []),
        ];
        console.log(dynamicFields, transformedFieldArray);

        setFieldsList(transformedFieldArray || []);

        if (Array.isArray(transformedFieldArray)) {
          const initialFormValues = {};
          const initialErrorValues = {};
          transformedFieldArray.forEach((field) => {
            if (!field) return;
            if (field?.type === "boolean") {
              if (typeof field?.default === "boolean") {
                initialFormValues[field?.name] = field?.default ? "yes" : "no";
              }
            } else {
              initialFormValues[field?.name] = field?.default;
            }
            initialErrorValues[field?.name] = false;
          });
          setFormValues(initialFormValues);
          setErrors(initialErrorValues);
        }
      } else {
        throw new Error();
      }
    } catch (err) {
      message.showToastMessage({
        message:
          err?.errors ||
          err?.message ||
          "Something went wrong while fetching fields. Try again!",
        variant: "error",
      });
      onCloseForm();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getFieldsList();
  }, []);

  const onCloseForm = () => {
    setFormValues({});
    setErrors({});
    handleCloseAction();
  };

  const validateForm = (form) => {
    const errorFields = {};
    const isEmpty = (value) =>
      value === "" ||
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0);

    const mandatoryFields = fieldsList?.map((field) => field?.name);
    fieldsList.forEach((field) => {

      if (field?.default === "all") {
        if (isEmpty(form[field?.name])) {
          errorFields[field?.name] = true;
        }
      } else {
        if (isEmpty(form[field?.name]) && field?.mandatory) {
          errorFields[field?.name] = true;
        }

      }


    });
    setErrors((prev) => ({ ...prev, ...errorFields }));
    return Object.keys(errorFields).length === 0;
  };

  const getFormPayload = (form) => {
    const formData = new FormData();
    const normalFields = fieldsList?.filter(
      (field) => field?.type !== "boolean"
    );
    normalFields.forEach((field) => {
      if (field?.type === "dropdown") {
        if (form[field?.name] !== "all") {
          const paramName = [
            "base_station_id",
            "line_customer_id",
            "user_id",
          ].includes(field?.name)
            ? field?.name
            : `params[${field?.name}]`;
          formData.append(paramName, form[field?.name] ? form[field?.name] : "");
        }
      } else if (field?.type === "date_time" || field?.type === "date") {

        formData.append(
          `params[${field?.name}]`, form[field?.name] ?
          moment(form[field?.name], "DD/MM/YYYY HH:mm:ss").format(
            "YYYY-MM-DD HH:mm:ss"
          ) : ""
        );



      } else {
        formData.append(`params[${field?.name}]`, form[field?.name] ? form[field?.name] : "");
      }
    });

    const booleanFields = fieldsList
      ?.filter((field) => field?.type === "boolean")
      ?.map((field) => field?.name);
    booleanFields.forEach((fieldName) =>
      formData.append(
        `params[${fieldName}]`,
        form[fieldName] === "yes" ? true : false
      )
    );
    formData.append("organisation_id", getOrganisationId());
    formData.append("report_id", currentSelection?.id);

    return formData;
  };

  const handleSubmitForm = async () => {
    setIsSubmitting(true);
    const isFormValid = validateForm(formValues);
    if (isFormValid) {
      try {
        const payload = getFormPayload(formValues);
        const response = await ReportsServices.generateReport(payload);
        if (response.success) {
          onCloseForm(true);
          refreshListing();

          setIsSubmitting(false);
          message.showToastMessage({
            message:
              response?.message ||
              "Report Generation Started! You will receive an email once it is ready",
            variant: "success",
          });
        }
      } catch (error) {
        setIsSubmitting(false);
        message.showToastMessage({
          message:
            error?.errors ||
            error?.message ||
            "Something went wrong while submitting the report.",
          variant: "error",
        });
      }
    } else {
      setIsSubmitting(false);
      message.showToastMessage({
        message: "Please fill all the mandatory fields",
        variant: "error",
      });
    }
  };

  const clearError = (formFieldId) => {
    setErrors((prev) => ({ ...prev, [formFieldId]: false }));
  };

  const handleChangeInput = (formFieldId, enteredValue) => {
    setFormValues((prev) => ({ ...prev, [formFieldId]: enteredValue }));
    clearError(formFieldId);
  };

  const handleSelectInput = (formFieldId, selected) => {
    setFormValues((prev) => ({ ...prev, [formFieldId]: selected }));
    clearError(formFieldId);
  };

  const handleToggleInput = (formFieldId, value) => {
    setFormValues((prev) => ({ ...prev, [formFieldId]: value }));
    clearError(formFieldId);
  };
  console.log(errors, "form errors");
  return (
    <Dialog maxWidth="md" open={true} onClose={onCloseForm}>
      <Box
        style={{
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          height: "500px",
          width: "600px",
        }}>
        <Typography variant="body1" color="primary">
          Report Setup
        </Typography>

        <Typography variant="body2">
          {reportsData?.description || ""}
        </Typography>

        {loading || isDropdownsLoading ? (
          <Box
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <LoadingIndicator />
          </Box>
        ) : (
          <Box style={{ width: "100%", flex: 1, overflowY: "scroll" }}>
            <Grid container style={{ width: "100%", maxWidth: "100%" }}>
              {fieldsList?.map((field) => {
                switch (field?.type) {
                  case "dropdown":
                    return (
                      <Grid
                        item
                        xs={6}
                        style={{
                          paddingRight: "0.25rem",
                          paddingLeft: "0.25rem",
                        }}>
                        <MROTextField
                          name={field?.name}
                          label={`${field?.title} *`}
                          select
                          InputLabelProps={{ shrink: true }}
                          error={errors[field?.name]}
                          onChange={(event) => {
                            console.log("valueee", event?.target?.value)
                            handleSelectInput(field?.name, event?.target?.value)
                          }
                          }
                          fullWidth
                          value={formValues[field?.name]}>
                          {dropdownOptions?.[field?.name]?.map((option) => (
                            <MenuItem value={option?.id}>
                              {option?.value}
                            </MenuItem>
                          )) || []}
                        </MROTextField>
                      </Grid>
                    );

                  case "boolean":
                    return (
                      <Grid
                        item
                        xs={6}
                        style={{
                          paddingRight: "0.25rem",
                          paddingLeft: "0.25rem",
                          display: "flex",
                          alignItems: "center",
                        }}>
                        <SMSReportToggle
                          toggleOptions={yesOrNoToggleOptions}
                          labelWidth="150px"
                          name={field?.name}
                          label={`${field?.title} ${field?.mandatory ? "*" : ""} `}
                          value={formValues[field?.name]}
                          handleToggle={(value) =>
                            handleToggleInput(field?.name, value)
                          }
                          error={field?.mandatory && errors[field?.name]}
                        />
                      </Grid>
                    );

                  case "date_time":
                    return (
                      <Grid
                        item
                        xs={6}
                        style={{
                          paddingRight: "0.25rem",
                          paddingLeft: "0.25rem",
                        }}>
                        <DateTimeField
                          label={`${field?.title} ${field?.mandatory ? "*" : ""} `}
                          helperText={
                            errors[field?.name] ? "* Invalid date and time" : ""
                          }
                          name={field?.name}
                          InputLabelProps={{ shrink: true }}
                          errors={field?.mandatory && errors[field?.name]}
                          type="planned"
                          defaultValue={formValues?.[field?.name] || ""}
                          placeholder={field?.description}
                          onChange={(event) =>
                            handleChangeInput(field?.name, event)
                          }
                        />
                      </Grid>
                    );

                  case "freetext":
                    return (
                      <Grid
                        item
                        xs={6}
                        style={{
                          paddingRight: "0.25rem",
                          paddingLeft: "0.25rem",
                        }}>
                        <MROTextField
                          name={field?.name}
                          label={`${field?.title}`}
                          InputLabelProps={{ shrink: true }}
                          placeholder={field?.description || ""}
                          error={field?.mandatory && errors[field?.name]}
                          value={formValues[field?.name]}
                          required={field?.mandatory || false}
                          onChange={(event) =>
                            handleChangeInput(field?.name, event?.target?.value)
                          }
                        />
                      </Grid>
                    );

                  case "integer":
                    return (
                      <Grid
                        item
                        xs={6}
                        style={{
                          paddingRight: "0.25rem",
                          paddingLeft: "0.25rem",
                        }}>
                        <MROTextField
                          name={field?.name}
                          label={`${field?.title}`}
                          placeholder={field?.description}
                          error={field?.mandatory && errors[field?.name]}
                          required={field?.mandatory || false}
                          onChange={(e) => {
                            const numericValue = e.target.value.replace(
                              /[^0-9]/g,
                              ""
                            );
                            handleChangeInput(
                              field?.name,
                              numericValue ? parseInt(numericValue) : ""
                            );
                          }}
                          InputLabelProps={{ shrink: true }}
                          value={
                            isNaN(formValues[field?.name])
                              ? ""
                              : formValues[field?.name]
                          }
                          fullWidth
                        />
                      </Grid>
                    );

                  case "numeric":
                    return (
                      <Grid
                        item
                        xs={6}
                        style={{
                          paddingRight: "0.25rem",
                          paddingLeft: "0.25rem",
                        }}>
                        <MROTextField
                          name={field?.name}
                          label={`${field?.title}`}
                          placeholder={field?.description}
                          error={field?.mandatory && errors[field?.name]}
                          onChange={(e) => {
                            let value = e.target.value;
                            if (/^\d*\.?\d{0,2}$/.test(value)) {
                              handleChangeInput(field?.name, value);
                            }
                          }}
                          required={field?.mandatory || false}
                          InputLabelProps={{ shrink: true }}
                          value={
                            isNaN(formValues[field?.name])
                              ? ""
                              : formValues[field?.name]
                          }
                          fullWidth
                        />
                      </Grid>
                    );

                  case "date":
                    return (
                      <Grid
                        item
                        xs={6}
                        style={{
                          paddingRight: "0.25rem",
                          paddingLeft: "0.25rem",
                        }}>
                        <DateField
                          label={`${field?.title} ${field?.mandatory ? "*" : ""}   `}
                          name={field?.name}
                          // format={"DD/MM/YYYY"}
                          defaultValue={formValues?.[field?.name] || ""}
                          error={field?.mandatory && Boolean(errors[field?.name])}
                          onChange={(selected) =>
                            handleChangeInput(field?.name, selected)
                          }
                          helperText={errors[field?.name] && "*Invalid date"}
                        />
                      </Grid>
                    );

                  case "time":
                    return (
                      <Grid
                        item
                        xs={6}
                        style={{
                          paddingRight: "0.25rem",
                          paddingLeft: "0.25rem",
                        }}>
                        <PlanningTimeField
                          required={field?.mandatory}
                          fullWidth
                          onlyTime
                          name={field?.name}
                          pointerEvent={"none"}
                          label={`${field?.title}`}
                          defaultValue={formValues[field?.name]}
                          format={"HH:mm"}
                          renderDate={"HH:mm"}
                          placeholder="hh:mm"
                          error={field?.mandatory && errors[field?.name]}
                          helperText={
                            errors[field?.name]
                              ? `*please enter ${field?.title?.toLowerCase()}`
                              : null
                          }
                          onChange={(e) => {
                            handleChangeInput(
                              field?.name,
                              moment(e, "DD/MM/YYYY HH:mm").format("HH:mm")
                            );
                          }}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    );
                }
              })}
            </Grid>
          </Box>
        )}
      </Box>

      <Box
        style={{
          padding: "1rem",
          display: "flex",
          justifyContent: "flex-end",
          gap: "1rem",
        }}>
        <MROButton
          disabled={loading}
          type="button"
          variant={"contained"}
          onClick={onCloseForm}>
          Cancel
        </MROButton>
        <MROButton
          loading={isSubmitting}
          disabled={loading}
          type="submit"
          variant={"contained"}
          color="primary"
          onClick={handleSubmitForm}>
          Complete
        </MROButton>
      </Box>
    </Dialog>
  );
};

export default ReportsAction;
